import { Injectable, OnDestroy, signal } from '@angular/core';
import { PROGRESS_ANIMATION, PROGRESS_BAR_CONSTANTS, PROGRESS_COMPUTED } from '../constants/progress-bar.constants';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private _progress = signal(PROGRESS_BAR_CONSTANTS.MIN_PROGRESS);
  private _isRefreshing = signal(false);
  private _isVisible = signal(false);

  private progressInterval: number | null = null;
  private hideTimeout: number | null = null;

  readonly progress = this._progress.asReadonly();
  readonly isRefreshing = this._isRefreshing.asReadonly();
  readonly isVisible = this._isVisible.asReadonly();

  startRefresh(): void {
    this.clearTimers();

    this._progress.set(PROGRESS_BAR_CONSTANTS.MIN_PROGRESS);
    this._isRefreshing.set(true);
    this._isVisible.set(true);
    this.animateProgress();
  }

  stopRefresh(): void {
    if (!this._isRefreshing()) return;

    this._isRefreshing.set(false);
    this.completeProgress();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private animateProgress(): void {
    this.progressInterval = window.setInterval(() => {
      const current = this._progress();

      if (current >= PROGRESS_ANIMATION.INITIAL_TARGET || !this._isRefreshing()) {
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        return;
      }

      this._progress.set(
        Math.min(current + PROGRESS_COMPUTED.INITIAL_STEP_INCREMENT, PROGRESS_ANIMATION.INITIAL_TARGET)
      );
    }, PROGRESS_COMPUTED.INITIAL_STEP_DURATION);
  }

  private completeProgress(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    const currentProgress = this._progress();
    const remaining = PROGRESS_BAR_CONSTANTS.MAX_PROGRESS - currentProgress;
    const stepIncrement = remaining / PROGRESS_ANIMATION.COMPLETION_STEPS;

    this.progressInterval = window.setInterval(() => {
      const current = this._progress();

      if (current >= PROGRESS_BAR_CONSTANTS.MAX_PROGRESS) {
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }

        this.scheduleHide();
        return;
      }

      this._progress.set(Math.min(current + stepIncrement, PROGRESS_BAR_CONSTANTS.MAX_PROGRESS));
    }, PROGRESS_COMPUTED.COMPLETION_STEP_DURATION);
  }

  private scheduleHide(): void {
    this.hideTimeout = window.setTimeout(() => {
      this._isVisible.set(false);
      this._progress.set(PROGRESS_BAR_CONSTANTS.MIN_PROGRESS);
      this.hideTimeout = null;
    }, PROGRESS_ANIMATION.HIDE_DELAY);
  }

  private clearTimers(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
